export type ConfigurationOption = {
  id: number
  name: string
  sku_code: string
  idx?: number
  css_hex?: string
  icon?: { thumb?: { url?: string } }
  has_price_modifier?: boolean
  price_modifier?: { price_modifier_cents: number }
}

export type ConfigurationSet = {
  variant_id: number
  configurable_variant_id?: number
  name: string
  display_name?: string
  display_type?: string
  sort_order?: number
  nested_data: readonly ConfigurationOption[]
}

export type ConfigurationSelections = Record<number, ConfigurationOption>
export type BundleOccurrenceId = number

export type BundleConfigurationItem = {
  bundle_group_product_id: BundleOccurrenceId
  name?: string
  product_name?: string
  is_configurable?: boolean
  is_configured?: boolean
  nested_data?: readonly ConfigurationSet[]
}

export type BundleSelections = Record<BundleOccurrenceId, { selected_options: ConfigurationSelections }>

type DefaultSelection = { variant_id: number; option: ConfigurationOption }

export type ConfigurationSelection = {
  selections: ConfigurationSelections
  defaults: readonly DefaultSelection[]
  missing: readonly ConfigurationSet[]
  complete: boolean
  skuSuffix: string
}

export const configurationLabel = (set: ConfigurationSet): string => set.display_name || set.name

export const configurationGuidance = (labels: readonly string[]): string => {
  if (labels.length === 0) return ''
  if (labels.length === 1) return `Please select ${labels[0]}.`
  if (labels.length === 2) return `Please select ${labels[0]} and ${labels[1]}.`
  return `Please select ${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}.`
}

export const resolveConfigurationSelection = (
  sets: readonly ConfigurationSet[],
  current: ConfigurationSelections
): ConfigurationSelection => {
  let selections = current
  const defaults: DefaultSelection[] = []

  const availabilityOrder = [...sets].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  sets.forEach(set => {
    if (selections[set.variant_id] || set.nested_data.length !== 1) return
    const option = { ...set.nested_data[0], idx: availabilityOrder.indexOf(set) }
    if (selections === current) selections = { ...current }
    selections[set.variant_id] = option
    defaults.push({ variant_id: set.variant_id, option })
  })

  const missing = sets.filter(set => !selections[set.variant_id])
  const skuSuffix = availabilityOrder
    .map(set => selections[set.variant_id]?.sku_code)
    .filter((code): code is string => typeof code === 'string')
    .map(code => `-${code}`)
    .join('')

  return { selections, defaults, missing, complete: missing.length === 0, skuSuffix }
}

export const chooseConfigurationOption = (
  sets: readonly ConfigurationSet[],
  current: ConfigurationSelections,
  variantId: number,
  optionId: number
): ConfigurationSelection => {
  const setIndex = sets.findIndex(set => set.variant_id === variantId)
  if (setIndex < 0) return resolveConfigurationSelection(sets, current)
  const option = sets[setIndex].nested_data.find(candidate => candidate.id === optionId)
  if (!option || current[variantId]?.id === option.id) return resolveConfigurationSelection(sets, current)
  return resolveConfigurationSelection(sets, {
    ...current,
    [variantId]: {
      ...option,
      idx: [...sets].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).indexOf(sets[setIndex])
    }
  })
}

export const missingBundleConfigurationLabels = (
  items: readonly BundleConfigurationItem[],
  selectionsByOccurrence: BundleSelections
): string[] => {
  const missing = items.flatMap(item => {
    if (!item.is_configurable || item.is_configured) return []
    const sets = item.nested_data?.filter(set => set.variant_id) ?? []
    const selections = selectionsByOccurrence[item.bundle_group_product_id]?.selected_options ?? {}
    return resolveConfigurationSelection(sets, selections).missing.map(set => ({
      label: configurationLabel(set),
      itemName: item.name || item.product_name || 'bundle item',
      occurrenceId: item.bundle_group_product_id
    }))
  })
  const labelCounts = new Map<string, number>()
  missing.forEach(({ label }) => labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1))
  const repeatedNames = new Map<string, number[]>()
  missing.forEach(({ label, itemName, occurrenceId }) => {
    const key = `${label}:${itemName}`
    const occurrences = repeatedNames.get(key) ?? []
    if (!occurrences.includes(occurrenceId)) occurrences.push(occurrenceId)
    repeatedNames.set(key, occurrences)
  })
  return missing.map(({ label, itemName, occurrenceId }) => {
    if ((labelCounts.get(label) ?? 0) < 2) return label
    const occurrences = repeatedNames.get(`${label}:${itemName}`) ?? []
    const context = occurrences.length > 1 ? `${itemName} (item ${occurrences.indexOf(occurrenceId) + 1})` : itemName
    return `${label} for ${context}`
  })
}
